import {FC} from 'react';
import {Box, Button, Flex, Stack} from '@chakra-ui/react';
import {api, IMemoryNote, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Textarea, useScreenSize} from 'ui-kit';

interface IProps {
  note: IMemoryNote | null;
  onClose: () => void;
}

interface INoteForm {
  text: string;
}

const NoteEditorModalFC: FC<IProps> = ({note, onClose}) => {
  const {data: settings} = api.settings.useSettingsQuery();
  const createMutation = api.memoryNotes.useCreateNoteMutation();
  const updateMutation = api.memoryNotes.useUpdateNoteMutation();

  const {t} = useT();
  const {isLessThan768} = useScreenSize();

  const maxLength = settings?.memory_notes?.max_note_length;

  const {
    control,
    handleSubmit,
    formState: {isValid}
  } = useForm<INoteForm>({
    mode: 'onChange',
    defaultValues: {text: note?.text ?? ''}
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: INoteForm) => {
    const text = data.text.trim();

    if (note) {
      updateMutation.mutate({id: note.id, text}, {onSuccess: onClose});
    } else {
      createMutation.mutate({text}, {onSuccess: onClose});
    }
  };

  return (
    <Stack h={'100%'} gap={'0'}>
      <Box flex={'1 1 auto'} overflowY={'auto'} p={isLessThan768 ? '16px' : '16px 24px'}>
        <Box mb={'6px'} color={'{colors.textSecondary}'}>
          {t('notes.fields.text')}
        </Box>
        <Controller
          name={'text'}
          control={control}
          rules={{
            validate: (v) => !!v.trim() || t('notes.validation.textRequired'),
            maxLength: maxLength
              ? {value: maxLength, message: t('notes.validation.textMaxLength', {count: maxLength})}
              : undefined
          }}
          render={({field}) => (
            <Textarea
              rows={6}
              size={'lg'}
              autoresize={false}
              value={field.value}
              placeholder={t('notes.placeholders.text')}
              onChange={field.onChange}
            />
          )}
        />
      </Box>

      <Flex
        gap={'12px'}
        justifyContent={'flex-end'}
        p={'24px 24px 12px 24px'}
        borderTop={'thin solid'}
        borderColor={'{colors.bgMain}'}
      >
        <Button
          size={isLessThan768 ? 'md' : 'lg'}
          variant={'subtle'}
          disabled={isPending}
          onClick={onClose}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          size={isLessThan768 ? 'md' : 'lg'}
          variant={'solid'}
          loading={isPending}
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
        >
          {note ? t('notes.actions.save') : t('notes.actions.create')}
        </Button>
      </Flex>
    </Stack>
  );
};

export const NoteEditorModal = NoteEditorModalFC;
